import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, Mail, Phone, Volume2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LINES = (name) => [
  `Hi ${name} — I'm Maya, HiBFF's AI Practice Manager.`,
  'I look after registration, onboarding and this private data room. Behind this door is everything you need to judge us properly — live numbers, the real documents, and a model you can play with yourself.',
  'Click the button below and step inside.',
];

const SPOKEN_TAIL = 'If you have any questions, or want to jump right in, email robert at hibff dot com, or call him on +44 7488 310834.';

export const DataRoomMayaWelcome = ({ name, token, onEnter, staticMode = false }) => {
  const firstName = String(name || 'there').split(' ')[0];
  const allLines = LINES(firstName);
  const [greeter, setGreeter] = useState(null);
  const [avatarIn, setAvatarIn] = useState(staticMode);
  const [lineIdx, setLineIdx] = useState(staticMode ? allLines.length : 0);
  const [typed, setTyped] = useState('');
  const [doneLines, setDoneLines] = useState(staticMode ? allLines : []);
  const [showContact, setShowContact] = useState(staticMode);
  const [leaving, setLeaving] = useState(staticMode);
  const [needsTap, setNeedsTap] = useState(false);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const msPerCharRef = useRef(30);

  // Revisit mode: mount off-screen, then roll down at the same 2.2s speed.
  useEffect(() => {
    if (!staticMode) return;
    const t = setTimeout(() => setLeaving(false), 60);
    return () => clearTimeout(t);
  }, [staticMode]);

  useEffect(() => {
    fetch(`${API_URL}/api/ava-video/registration-greeter`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => setGreeter(d || {}))
      .catch(() => setGreeter({}));
    const t = setTimeout(() => setAvatarIn(true), 250);
    return () => clearTimeout(t);
  }, []);

  // Maya's voice — one narration covering the whole welcome, synced to the typewriter.
  useEffect(() => {
    if (!token || staticMode) return;
    let cancelled = false;
    const spokenText = [...LINES(firstName), SPOKEN_TAIL].join(' ');
    fetch(`${API_URL}/api/tts/dataroom`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Dataroom-Token': token },
      body: JSON.stringify({ text: spokenText, voice: 'nova' }),
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (cancelled || !d?.success || !d.audio) return;
        const audio = new Audio(`data:audio/mp3;base64,${d.audio}`);
        audioRef.current = audio;
        audio.addEventListener('loadedmetadata', () => {
          const totalChars = spokenText.length;
          if (audio.duration && totalChars) {
            msPerCharRef.current = Math.min(60, Math.max(20, ((audio.duration * 1000) - 1500) / totalChars));
          }
        });
        audio.play().catch(() => { if (!cancelled) setNeedsTap(true); });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, [token, firstName]);

  const tapToListen = () => {
    setNeedsTap(false);
    if (audioRef.current) audioRef.current.play().catch(() => {});
  };

  useEffect(() => {
    if (staticMode) return;
    const lines = LINES(firstName);
    if (lineIdx >= lines.length) { setShowContact(true); return; }
    const line = lines[lineIdx];
    let i = 0;
    timerRef.current = setInterval(() => {
      i++;
      setTyped(line.slice(0, i));
      if (i >= line.length) {
        clearInterval(timerRef.current);
        setTimeout(() => {
          setDoneLines(prev => [...prev, line]);
          setTyped('');
          setLineIdx(n => n + 1);
        }, 420);
      }
    }, msPerCharRef.current);
    return () => clearInterval(timerRef.current);
  }, [lineIdx, firstName]);

  const handleEnter = () => {
    if (audioRef.current) { audioRef.current.pause(); }
    setLeaving(true);
    setTimeout(() => onEnter(), 2200);
  };

  return (
    <div
      className="fixed inset-0 z-[9998] flex flex-col items-center justify-center px-6 text-center overflow-y-auto"
      style={{
        background: 'linear-gradient(135deg, #FDF4EC 0%, #FBEFF2 55%, #F4EDFB 100%)',
        transform: leaving ? 'translateY(-100%)' : 'translateY(0)',
        transition: 'transform 2.2s cubic-bezier(0.45, 0.05, 0.25, 1)',
        boxShadow: '0 14px 40px rgba(26,18,9,0.25)',
      }}
      data-testid="dr-maya-welcome"
    >
      <div className="max-w-xl w-full py-10">
        {/* Maya — animated (idle video loop when available, breathing portrait otherwise) */}
        <div
          className="relative mx-auto mb-4 w-40 h-40 sm:w-44 sm:h-44"
          style={{
            opacity: avatarIn ? 1 : 0,
            transform: avatarIn ? 'scale(1)' : 'scale(0.85)',
            transition: 'opacity 0.7s cubic-bezier(0.16,1,0.3,1), transform 0.7s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          <div className="w-full h-full rounded-3xl overflow-hidden shadow-xl ring-4 ring-white" style={{ animation: 'maya-breathe 4.5s ease-in-out infinite' }}>
            {greeter?.idle_loop_url ? (
              <video
                src={`${API_URL}/api/files/${greeter.idle_loop_url}`}
                poster={greeter.avatar_url || undefined}
                autoPlay loop muted playsInline
                className="w-full h-full object-cover"
                data-testid="dr-maya-video"
              />
            ) : greeter?.avatar_url ? (
              <img src={greeter.avatar_url} alt="Maya" className="w-full h-full object-cover" data-testid="dr-maya-avatar" />
            ) : (
              <div className="w-full h-full bg-purple-100" />
            )}
          </div>
          <span className="absolute bottom-2 right-2 w-4 h-4 rounded-full bg-emerald-400 ring-2 ring-white" style={{ animation: 'maya-pulse 2s ease-in-out infinite' }} />
        </div>

        <p className="text-lg font-black text-[#1A1209]">Maya</p>
        <p className="text-[10px] font-bold text-[#7B4FD8] uppercase tracking-[0.2em] mb-6">HiBFF's AI Practice Manager</p>

        {needsTap && (
          <button onClick={tapToListen}
            className="inline-flex items-center gap-1.5 mb-4 px-4 py-2 rounded-full bg-white border border-[#7B4FD8]/30 text-xs font-bold text-[#7B4FD8] shadow-sm hover:bg-purple-50"
            data-testid="dr-maya-listen-btn">
            <Volume2 size={14} /> Tap to hear Maya
          </button>
        )}

        {/* Typed welcome */}
        <div className="min-h-[130px] space-y-3">
          {doneLines.map((l, i) => (
            <p key={i} className={`${i === 0 ? 'text-xl sm:text-2xl font-bold text-[#1A1209]' : 'text-sm text-gray-600'} leading-relaxed`} style={{ fontFamily: i === 0 ? 'Georgia, serif' : undefined }}>{l}</p>
          ))}
          {typed && (
            <p className={`${doneLines.length === 0 ? 'text-xl sm:text-2xl font-bold text-[#1A1209]' : 'text-sm text-gray-600'} leading-relaxed`} style={{ fontFamily: doneLines.length === 0 ? 'Georgia, serif' : undefined }}>
              {typed}<span className="inline-block w-[2px] h-[1em] align-middle ml-0.5 bg-[#7B4FD8]" style={{ animation: 'maya-blink 0.7s step-end infinite' }} />
            </p>
          )}
        </div>

        {/* Enter button */}
        <button
          onClick={handleEnter}
          className="mt-6 inline-flex items-center gap-2 px-9 py-3.5 rounded-xl font-bold text-white shadow-lg hover:shadow-xl bg-[#7B4FD8] hover:bg-[#6a3fc7]"
          style={{
            opacity: showContact ? 1 : 0,
            transform: showContact ? 'translateY(0)' : 'translateY(14px)',
            transition: 'opacity 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1), background-color 0.2s ease, box-shadow 0.2s ease',
            pointerEvents: showContact ? 'auto' : 'none',
          }}
          data-testid="dr-maya-enter-btn"
        >
          Enter the Data Room <ChevronRight size={17} />
        </button>

        {/* Contact sign-off */}
        <div
          className="mt-7"
          style={{ opacity: showContact ? 1 : 0, transition: 'opacity 0.6s ease 0.3s' }}
          data-testid="dr-maya-contact"
        >
          <p className="text-xs text-gray-500 leading-relaxed max-w-md mx-auto">
            If you have any questions — or want to jump right in — Robert would love to hear from you:
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 mt-2.5">
            <a href="mailto:robert@hibff.com" className="inline-flex items-center gap-1.5 text-xs font-bold text-[#7B4FD8] hover:underline" data-testid="dr-maya-email">
              <Mail size={13} /> robert@hibff.com
            </a>
            <a href="tel:+447488310834" className="inline-flex items-center gap-1.5 text-xs font-bold text-[#7B4FD8] hover:underline" data-testid="dr-maya-phone">
              <Phone size={13} /> +44 (0)7488 310834
            </a>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes maya-breathe { 0%,100% { transform: scale(1); } 50% { transform: scale(1.025); } }
        @keyframes maya-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.6; transform: scale(0.85); } }
        @keyframes maya-blink { 0%,100% { opacity: 1; } 50% { opacity: 0; } }
      `}</style>
    </div>
  );
};

export default DataRoomMayaWelcome;
